import { z } from "zod/v4-mini";

import { FileModel, fileSchema } from "@/models/FileModel.ts";
import { BasePractitionerUser, basePractitionerUserSchema } from "@/models/UserModel.ts";
import type { PaletteColors } from "@/theme/PaletteOverrides.ts";
import { Schema, cz } from "@/utils/zodUtils.ts";

import { TeleMonitoringCriteria, telemonitoringCriteriaSchema } from "./TelemonitoringCriteriaModel";

export const telemonitoringTierSchema = z.literal(["base", "level1", "level2"]);
export type TelemonitoringTier = z.infer<typeof telemonitoringTierSchema>;
export const telemonitoringTagsSchema = z.literal([
  "active",
  "expiring_soon",
  "expired",
  "expired_recently",
  "gestational_no_insulin",
  "no_glycemia_data",
  "nurse_asked_for_reinclusion",
]);
export type TelemonitoringTags = z.infer<typeof telemonitoringTagsSchema>;
export const patientRenewalDecisionSchema = z.literal(["do_not_renew", "decide_later"]);
export type PatientRenewalDecision = z.infer<typeof patientRenewalDecisionSchema>;

export type CarePlan = {
  id: string;
  periodStart: string;
  periodEnd: string;
  description?: string;
  descriptionAttachment?: FileModel;
  telemonitoringTier: TelemonitoringTier;
  telemonitoringCriteria?: TeleMonitoringCriteria;
  subjectId: string;
  doctor: BasePractitionerUser;
  nurse?: BasePractitionerUser;
  prescription?: FileModel;
  treatmentPrescription?: FileModel;
  referralLetter?: FileModel;
};

export const carePlanSchema: Schema<CarePlan> = cz.camelCaseObject({
  id: z.string(),
  period_start: z.iso.date(),
  period_end: z.iso.date(),
  description: cz.optional(z.string()),
  description_attachment: cz.optional(fileSchema),
  telemonitoring_tier: telemonitoringTierSchema,
  telemonitoring_criteria: cz.optional(telemonitoringCriteriaSchema),
  subject_id: z.string(),
  doctor: basePractitionerUserSchema,
  nurse: cz.optional(basePractitionerUserSchema),
  prescription: cz.optional(fileSchema),
  treatment_prescription: cz.optional(fileSchema),
  referral_letter: cz.optional(fileSchema),
});

export type CurrentCarePlanInfo = {
  currentCarePlan?: CarePlan;
  telemonitoringTags: TelemonitoringTags[];
};

export const currentCarePlanInfoSchema: Schema<CurrentCarePlanInfo> = cz.camelCaseObject({
  current_care_plan: cz.optional(carePlanSchema),
  telemonitoring_tags: cz.forcedArray(telemonitoringTagsSchema),
});

export const getTelemonitoringTierTranslation = (tier: TelemonitoringTier) => {
  return {
    base: "diabetesForms.criteriaBase",
    level1: "diabetesForms.criteriaLevel1",
    level2: "diabetesForms.criteriaLevel2",
  }[tier];
};

export const getTelemonitoringTierColor = (tier: TelemonitoringTier): PaletteColors => {
  return (
    {
      base: "green",
      level1: "yellow",
      level2: "red",
    } satisfies Record<TelemonitoringTier, PaletteColors>
  )[tier];
};

export const getTelemonitoringTierDescription = (tier: TelemonitoringTier) => {
  return {
    base: "diabetesForms.criteriaMeaningBase",
    level1: "diabetesForms.criteriaMeaningLevel1",
    level2: "diabetesForms.criteriaMeaningLevel2",
  }[tier];
};
